import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
  FavoriteBorder as LikeIcon,
  ChatBubbleOutline as CommentIcon,
  Repeat as ShareIcon,
  VisibilityOutlined as ImpressionIcon,
  CampaignOutlined as PromotedIcon,
} from '@mui/icons-material';
import { getPlatformName } from '../config/platforms';
import { PlatformResult } from './PlatformOutcomes';

/**
 * Likes, comments and shares a post actually got, per platform.
 *
 * The figures come from `post_analytics`, refreshed hourly from each network's own API. This
 * only displays them — nothing here fetches.
 *
 * Three distinctions the component exists to preserve, each of which has been got wrong in
 * this codebase before:
 *
 * 1. **Never measured is not zero.** A result with no metrics row reads "not measured yet"
 *    rather than "0 likes", which would claim nobody engaged. The hourly refresh only covers
 *    posts published in the last 30 days, so older posts stay unmeasured permanently.
 * 2. **A platform that does not report a figure shows an em dash, not 0.** Facebook needs
 *    `read_insights` for impressions and LinkedIn does not expose them for personal posts;
 *    printing 0 there reads as "nobody saw this".
 * 3. **Only successful results have engagement.** A rejected or failed platform never reached
 *    anyone, and a removed post is gone from the platform, so neither shows figures.
 * 4. **Instagram figures can understate a boosted post badly.** 3 likes here against 105 in
 *    the Instagram app, on the same post, observed 2026-10-05. The paid engagement is not in
 *    anything this API returns. There is **no reliable way to detect it from reach and views**
 *    — a boost does not move either one, and on the two known-boosted posts reach was the
 *    *lowest* of any post on the account. A ratio heuristic was tried and did not separate
 *    boosted from organic at all. See docs/memory/shoutvector-boosted-post-metrics.md.
 */

interface Props {
  results?: PlatformResult[];
  /** `compact` is the one-line summary for a history card; `detail` is the per-platform list. */
  variant?: 'compact' | 'detail';
}

/**
 * The paid counterpart of each organic metric.
 *
 * Instagram reports only likes, comments and shares against an ad; `impressions` has no paid
 * twin here because the organic `impressions` column is already dead for Instagram (Meta
 * withdrew it in v22) and `paid_impressions` is reported separately in the boost line.
 */
const PAID_KEY: Partial<Record<'likes' | 'comments' | 'shares' | 'impressions', 'paid_likes' | 'paid_comments' | 'paid_shares'>> = {
  likes: 'paid_likes',
  comments: 'paid_comments',
  shares: 'paid_shares',
};

const METRICS = [
  { key: 'likes' as const, label: 'likes', Icon: LikeIcon },
  { key: 'comments' as const, label: 'comments', Icon: CommentIcon },
  { key: 'shares' as const, label: 'reposts', Icon: ShareIcon },
  { key: 'impressions' as const, label: 'impressions', Icon: ImpressionIcon },
];

/**
 * What each platform calls its reach figure. X reports `impression_count` and labels it
 * **views** in its own UI, so calling it "impressions" here would not match what you see on
 * the post itself.
 *
 * It is also the only platform in this app that reports the figure per post: Facebook removed
 * post-level impressions outright (every variant errors, with read_insights granted), and
 * LinkedIn exposes them org-wide rather than per share. So an em dash on those two is
 * permanent, not a setup problem to chase.
 */
const reachLabel = (platform: string) => (platform === 'twitter' ? 'views' : 'impressions');

/**
 * The three shown on a history card. Impressions are deliberately excluded: more platforms
 * withhold the figure than report it, so a headline total would misrepresent reach.
 */
const COMPACT_METRICS = METRICS.filter(
  (m): m is typeof m & { key: 'likes' | 'comments' | 'shares' } => m.key !== 'impressions'
);

/** Results that can carry engagement: published, and still live on the platform. */
const engageable = (results: PlatformResult[]) => results.filter(r => r.status === 'success');

const num = (n?: number | null) => (typeof n === 'number' ? n.toLocaleString() : null);

export const PostEngagement: React.FC<Props> = ({ results = [], variant = 'compact' }) => {
  const live = engageable(results);
  if (live.length === 0) return null;

  const measured = live.filter(r => r.measured);

  if (variant === 'compact') {
    if (measured.length === 0) return null;

    // Organic plus paid, because that is the number the platform itself shows — a card
    // reading 3 on a post displaying 105 is what started this. The split stays in the
    // tooltip and the detail view, so "what did the boost buy" is still answerable.
    const organic = {
      likes: measured.reduce((n, r) => n + (r.likes || 0), 0),
      comments: measured.reduce((n, r) => n + (r.comments || 0), 0),
      shares: measured.reduce((n, r) => n + (r.shares || 0), 0),
    };
    const paid = {
      likes: measured.reduce((n, r) => n + (r.paid_likes || 0), 0),
      comments: measured.reduce((n, r) => n + (r.paid_comments || 0), 0),
      shares: measured.reduce((n, r) => n + (r.paid_shares || 0), 0),
    };
    const anyPaid = measured.some(r => r.promoted);
    const totals = {
      likes: organic.likes + paid.likes,
      comments: organic.comments + paid.comments,
      shares: organic.shares + paid.shares,
    };

    // Views come from X alone, so this is that one platform's figure rather than a total —
    // which is precisely why it can be shown here. Summing reach across platforms would be
    // meaningless when only one of them reports it.
    const xResult = measured.find(r => r.platform === 'twitter');
    const views = typeof xResult?.impressions === 'number' ? xResult.impressions : null;

    // Nothing worth a row of zeros — the detail view still shows the breakdown.
    if (totals.likes + totals.comments + totals.shares === 0 && views === null) return null;

    const unmeasured = live.length - measured.length;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary' }}>
        <Tooltip
          arrow
          title={
            <>
              {unmeasured > 0
                ? `Across ${measured.length} of ${live.length} platforms — ${unmeasured} not measured yet`
                : `Across ${measured.map(r => getPlatformName(r.platform)).join(', ')}`}
              {/* Where each number comes from. The headline is organic + paid combined, so
                  saying which part is which is what keeps it honest. */}
              {anyPaid && (
                <>
                  <br />
                  Likes: {organic.likes.toLocaleString()} from the post
                  {' + '}{paid.likes.toLocaleString()} from the boost
                  {(organic.comments + paid.comments) > 0 && (
                    <>
                      <br />
                      Comments: {organic.comments.toLocaleString()} from the post
                      {' + '}{paid.comments.toLocaleString()} from the boost
                    </>
                  )}
                  {(organic.shares + paid.shares) > 0 && (
                    <>
                      <br />
                      Shares: {organic.shares.toLocaleString()} from the post
                      {' + '}{paid.shares.toLocaleString()} from the boost
                    </>
                  )}
                </>
              )}
              {!anyPaid && measured.some(r => r.platform === 'instagram') && (
                <>
                  <br />
                  Organic — this post was not boosted.
                </>
              )}
            </>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {COMPACT_METRICS.map(({ key, Icon }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Icon sx={{ fontSize: 15 }} />
                <Typography variant="caption">{totals[key].toLocaleString()}</Typography>
              </Box>
            ))}
          </Box>
        </Tooltip>

        {/* Separate tooltip: this number is X's alone, and saying so stops it reading as a
            total like the three beside it. */}
        {views !== null && (
          <Tooltip arrow title={`${views.toLocaleString()} views on X`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <ImpressionIcon sx={{ fontSize: 15 }} />
              <Typography variant="caption">{views.toLocaleString()}</Typography>
            </Box>
          </Tooltip>
        )}

      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {live.map(result => (
        <Box
          key={result.platform}
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}
        >
          <Typography variant="body2" sx={{ minWidth: 110, fontWeight: 500 }}>
            {getPlatformName(result.platform)}
          </Typography>

          {result.measured ? (
            METRICS.map(({ key, label, Icon }) => {
              // Organic + paid, matching what the platform's own app displays. The split is
              // kept in the tooltip rather than dropped: "106" answers "how did this post do"
              // and "4 organic + 102 from the boost" answers "what did the spend buy".
              const paidKey = PAID_KEY[key];
              const paidValue = paidKey ? result[paidKey] : null;
              const organicValue = result[key];
              const combined =
                typeof organicValue === 'number' || typeof paidValue === 'number'
                  ? (organicValue || 0) + (paidValue || 0)
                  : null;

              const value = num(combined);
              const shown = key === 'impressions' ? reachLabel(result.platform) : label;

              // Name the source of every figure, so no number on this screen is unexplained.
              const title = value === null
                ? `${shown} not reported by ${getPlatformName(result.platform)}`
                : typeof paidValue === 'number' && paidValue > 0
                  ? `${shown} — ${(organicValue || 0).toLocaleString()} organic from the post, `
                    + `${paidValue.toLocaleString()} from the boost`
                  : `${shown} — organic, from the post itself`;

              return (
                <Tooltip key={key} arrow title={title}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.4,
                      color: value === null ? 'text.disabled' : 'text.secondary',
                    }}
                  >
                    <Icon sx={{ fontSize: 16 }} />
                    <Typography variant="body2">{value ?? '—'}</Typography>
                    {/* A quiet marker that this figure includes paid delivery, so a combined
                        number is never mistaken for pure organic performance. */}
                    {typeof paidValue === 'number' && paidValue > 0 && (
                      <PromotedIcon sx={{ fontSize: 13, opacity: 0.6 }} />
                    )}
                  </Box>
                </Tooltip>
              );
            })
          ) : (
            <Typography variant="body2" color="text.disabled">
              Not measured yet
            </Typography>
          )}

          {/* Reach sits next to views deliberately: the gap between them is what shows a post
              was boosted, and it is the only honest signal we have without ads_read. */}
          {result.measured && typeof result.reach === 'number' && (
            <Tooltip arrow title="accounts reached organically">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
                <Typography variant="body2">{result.reach.toLocaleString()} reached</Typography>
              </Box>
            </Tooltip>
          )}

          {/* What the boost added, on its own line. Separate from the organic figures rather
              than folded in, so the spend can be judged against what it bought. */}
          {result.promoted === 1 && (
            <Tooltip
              arrow
              title={
                `Paid: ${(result.paid_likes ?? 0).toLocaleString()} likes · ` +
                `${(result.paid_reach ?? 0).toLocaleString()} reached · ` +
                `${(result.paid_impressions ?? 0).toLocaleString()} impressions` +
                (result.paid_clicks ? ` · ${result.paid_clicks.toLocaleString()} clicks` : '')
              }
            >
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  color: 'primary.main', fontWeight: 500,
                }}
              >
                <PromotedIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  of which {(result.paid_likes ?? 0).toLocaleString()} from the boost
                  {typeof result.paid_spend === 'number' && result.paid_spend > 0 &&
                    ` · $${result.paid_spend.toFixed(2)} spent`}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      ))}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        Refreshed hourly from each platform. An em dash means the platform does not report that
        figure — only X reports views per post.
        {live.some(r => r.promoted === 1) && (
          <>
            {' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              This post was boosted.
            </Box>{' '}
            The figures above combine engagement on the post itself with engagement the advert
            delivered — the same total the network&apos;s own app shows. Hover any figure for the
            split, or read the boost line for what the advert added and what it cost.
            {live.some(r => r.promoted === 1 && r.platform === 'linkedin') &&
              ' LinkedIn boost figures come from the Campaign Manager report imported on Stats.'}
          </>
        )}
      </Typography>
    </Box>
  );
};
